import { useState, type ReactNode } from 'react';
import type { NewItemImportPayload } from '../api/new_items';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { fmtDateTime, fmtPrice } from '../utils/format';

// Human-readable labels for NewItem.dims_source (BOT-352) — which tier of
// shared.dims.resolve_dims's cascade set weight/dims.
const DIMS_SOURCE_LABELS: Record<string, string> = {
  joes_rules: "Joe's rules",
  website: 'Website data',
  ai: 'AI estimate',
  price_range: 'Price range',
};

/** Empty return status means returnable by default — never label that case. */
export function ReturnBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs text-zinc-300">—</span>;
  return <Badge variant="danger" title={status}>Non-returnable</Badge>;
}

/**
 * The full NetSuite import payload for a new-item candidate.
 *
 * This is the answer to "show me everything that used to be in the CSV import"
 * (BOT-392) — the fields listed here are exactly what will be created in
 * NetSuite, read-only, with no editing step in between.
 */
export default function NewItemPayloadPanel({
  payload: p,
  dimsSource,
  dimsSourceDetail,
  editable = false,
  onDimsSave,
}: {
  payload: NewItemImportPayload;
  dimsSource: string | null;
  dimsSourceDetail: string | null;
  editable?: boolean;
  onDimsSave?: (dims: { height: number; width: number; length: number; weight: number }) => Promise<void>;
}) {
  const dims =
    p.height_inch != null && p.width_inch != null && p.length_inch != null
      ? `${p.height_inch} × ${p.width_inch} × ${p.length_inch} in`
      : null;
  const [dimsSourceLabelOverride, setDimsSourceLabelOverride] = useState<string | null>(null);
  const dimsSourceLabel =
    dimsSourceLabelOverride ?? (dimsSource ? DIMS_SOURCE_LABELS[dimsSource] ?? dimsSource : null);

  const [height, setHeight] = useState(String(p.height_inch ?? 0));
  const [width, setWidth] = useState(String(p.width_inch ?? 0));
  const [length, setLength] = useState(String(p.length_inch ?? 0));
  const [weight, setWeight] = useState(String(p.item_weight ?? 0));
  const [saving, setSaving] = useState(false);
  const dirty =
    height !== String(p.height_inch ?? 0) ||
    width !== String(p.width_inch ?? 0) ||
    length !== String(p.length_inch ?? 0) ||
    weight !== String(p.item_weight ?? 0);

  const handleSave = async () => {
    if (!onDimsSave || saving) return;
    setSaving(true);
    try {
      await onDimsSave({
        height: Number(height) || 0,
        width: Number(width) || 0,
        length: Number(length) || 0,
        weight: Number(weight) || 0,
      });
      setDimsSourceLabelOverride('Manual');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-x-10 gap-y-5 py-4 pl-[84px] pr-5 sm:grid-cols-2 xl:grid-cols-3">
      <PanelSection title="Pricing">
        <Field label="Cost source" value={p.cost_source} />
        <Field label="Vendor 1 name" value={p.vendor1_name} />
        <Field
          label="Vendor 1 preferred"
          value={p.vendor1_preferred == null ? null : p.vendor1_preferred ? 'Yes' : 'No'}
        />
        <Field
          label="Vendor 1 purchase price"
          value={p.vendor1_purchase_price != null ? fmtPrice(p.vendor1_purchase_price) : null}
        />
      </PanelSection>

      <PanelSection title="Returns & core">
        <Field label="Return status" value={p.return_status ? <ReturnBadge status={p.return_status} /> : null} />
        <Field label="Core charge" value={p.core_charge ? 'Yes' : null} />
        <Field
          label="Core charge amount"
          value={p.core_charge_amount != null ? fmtPrice(p.core_charge_amount) : null}
        />
      </PanelSection>

      <PanelSection title="Attributes">
        <Field label="Manufacturer" value={p.manufacturer} />
        <Field label="Product type" value={p.product_type} />
        <Field label="Country of manufacture" value={p.country_of_manufacture} />
        <Field label="Made in USA" value={p.made_in_usa == null ? null : p.made_in_usa ? 'Yes' : 'No'} />
        <Field label="Warranty" value={p.warranty} />
        <Field label="Home value (lead time)" value={p.home_value} />
      </PanelSection>

      {editable ? (
        <section className="ring-1 ring-blue-200 rounded-lg p-3">
          <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.04em] text-zinc-400">Dimensions</h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <DimsInput label="Height (in)" value={height} onChange={setHeight} />
            <DimsInput label="Width (in)" value={width} onChange={setWidth} />
            <DimsInput label="Length (in)" value={length} onChange={setLength} />
            <DimsInput label="Weight (lb)" value={weight} onChange={setWeight} />
          </div>
          <Field label="Source" value={dimsSourceLabel} title={dimsSourceDetail ?? undefined} />
          {dimsSourceDetail && <Field label="Source detail" value={dimsSourceDetail} />}
          <div className="mt-3">
            <Button size="sm" disabled={!dirty || saving} onClick={handleSave}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </section>
      ) : (
        <PanelSection title="Dimensions">
          <Field label="Item weight" value={p.item_weight} />
          <Field label="H × W × L" value={dims} />
          <Field label="Source" value={dimsSourceLabel} title={dimsSourceDetail ?? undefined} />
          {dimsSourceDetail && <Field label="Source detail" value={dimsSourceDetail} />}
        </PanelSection>
      )}

      <PanelSection title="Descriptions" columns={1}>
        <Field label="Purchase description" value={p.purchase_description} />
        <Field label="Sales description" value={p.sales_description} />
      </PanelSection>

      <PanelSection title="Source">
        <Field
          label="Vendor URL"
          value={
            p.url ? (
              <a
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block max-w-[260px] truncate underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-500"
                title={p.url}
              >
                {p.url}
              </a>
            ) : null
          }
        />
        <Field label="SKU source" value={p.sku_source} />
        <Field
          label="Scraped (debug time_stamp)"
          value={p.debug_time_stamp ? fmtDateTime(p.debug_time_stamp) : null}
          title={p.debug_time_stamp ?? undefined}
        />
      </PanelSection>
    </div>
  );
}

function PanelSection({
  title,
  columns = 2,
  children,
}: {
  title: string;
  columns?: 1 | 2;
  children: ReactNode;
}) {
  return (
    <section>
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.04em] text-zinc-400">{title}</h3>
      <dl className={`grid gap-x-4 gap-y-3 ${columns === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>{children}</dl>
    </section>
  );
}

function DimsInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label className="mb-1 block text-[11px] text-muted-foreground">{label}</label>
      <input
        type="number"
        min="0"
        step="any"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={(e) => e.target.select()}
        className="w-full rounded border border-zinc-200 px-2 py-1 font-mono text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
      />
    </div>
  );
}

function Field({ label, value, title }: { label: string; value: ReactNode; title?: string }) {
  const empty = value == null || value === '';
  return (
    <div>
      <dt className="text-[11px] text-muted-foreground">{label}</dt>
      <dd className={`text-[12.5px] ${empty ? 'text-zinc-300' : 'text-foreground'}`} title={title}>
        {empty ? '—' : value}
      </dd>
    </div>
  );
}
